import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

import { useThemeContext } from '../../../../contexts/BaseThemeContextProvider';
import { AccountType } from '../../../../types/account';

type AccountNameDescriptionVariant = 'banner' | 'card' | 'both' | 'header';

interface AccountNameDescriptionProps {
  account: AccountType;
  variant?: AccountNameDescriptionVariant;
  actionButton?: React.ReactNode;
}

export interface ContactBlockProps {
  title: string;
  name: string | null;
  email: string | null;
  phone: string | null;
}

export function ContactBlock({ title, name, email, phone }: ContactBlockProps): JSX.Element | null {
  const hasContent = Boolean(name || email || phone);

  if (!hasContent) {
    return null;
  }

  return (
    <Box sx={{ mb: 0 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {name ? (
        <Typography variant="body2" sx={{ mb: 0.25 }}>
          {name}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
        {email ? (
          <Tooltip title={`Email: ${email}`}>
            <IconButton
              component="a"
              href={`mailto:${email}`}
              size="small"
              sx={{ p: 0.5, backgroundColor: 'action.hover' }}
            >
              <MailOutlineIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        ) : null}
        {phone ? (
          <Tooltip title={`Copy phone: ${phone}`}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(phone);
              }}
              sx={{ p: 0.5, backgroundColor: 'action.hover' }}
            >
              <PhoneOutlinedIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
}

export interface AddressBlockProps {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export function AddressBlock({ line1, line2, city, state, zip }: AddressBlockProps): JSX.Element | null {
  const addressLines = [line1, line2].filter(Boolean) as string[];
  const locationBits = [city, state, zip].filter(Boolean).join(', ');

  if (locationBits) {
    addressLines.push(locationBits);
  }

  if (!addressLines.length) {
    return null;
  }

  return (
    <Box sx={{ mb: 0 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        Address
      </Typography>
      {addressLines.map((line) => (
        <Typography key={line} variant="body2" color="text.secondary">
          {line}
        </Typography>
      ))}
    </Box>
  );
}

export default function AccountNameDescription({
  account,
  variant = 'banner',
  actionButton,
}: AccountNameDescriptionProps): JSX.Element {
  const { theme } = useThemeContext();
  const hasDescription = Boolean(account.description?.trim());
  const description = hasDescription ? account.description : 'No description provided';
  const showBanner = variant === 'banner' || variant === 'both' || variant === 'header';
  const showCard = variant === 'card' || variant === 'both';
  const showDescription = variant !== 'header';

  const displayName = account.name;

  return (
    <>
      {showBanner ? (
        <Box
          sx={{
            backgroundColor: variant === 'header' ? 'transparent' : theme.layout.defaults.backgroundColor,
            borderRadius: '16px',
            padding: variant === 'header' ? '0px 12px' : '12px',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: theme.custom.components.headerBanner.title.color,
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '28px',
              marginBottom: '8px',
              letterSpacing: '-0.25px',
              whiteSpace: variant === 'header' ? 'nowrap' : 'normal',
              textOverflow: variant === 'header' ? 'ellipsis' : 'initial',
              overflow: variant === 'header' ? 'hidden' : 'initial',
            }}
          >
            {displayName}
          </Typography>
          {showDescription ? (
            <Typography variant="body1" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {showCard ? (
        <Box sx={{ display: 'grid', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                {account.name}
              </Typography>

              {hasDescription ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 0.5,
                  }}
                >
                  {account.description}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 0.5,
                  }}
                >
                  No description provided
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>{actionButton}</Box>
          </Box>
        </Box>
      ) : null}
    </>
  );
}
